<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type { IdeaWithVotes, SortOption } from '../types';
import Heart from './icons/Heart.vue';
import AlertCircle from './icons/AlertCircle.vue';
import HandHelping from './icons/HandHelping.vue';
import Edit from './icons/Edit.vue';
import Trash2 from './icons/Trash2.vue';
import MessageSquare from './icons/MessageSquare.vue';
import ConfirmDialog from './ConfirmDialog.vue';

const { t } = useI18n();

interface Props {
  ideas: IdeaWithVotes[];
  visibleVoteTypes?: ('liked' | 'important' | 'supported')[];
  currentUserId?: string;
  userRole?: 'owner' | 'member' | 'reader' | null;
  sortBy?: SortOption;
  votesLocked?: boolean;
  commentsEnabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  visibleVoteTypes: () => ['liked', 'important', 'supported'],
  votesLocked: false,
  commentsEnabled: true,
});

const emit = defineEmits({
  vote: (
    _id: string,
    _type: 'liked' | 'important' | 'supported',
    _onError: (() => void) | undefined
  ) => true,
  edit: (_id: string) => true,
  delete: (_id: string) => true,
  tagClick: (_tag: string) => true,
  'update:sortBy': (_sort: SortOption) => true,
  comment: (_id: string) => true,
});

const canEditIdea = (idea: IdeaWithVotes) => {
  if (props.userRole === 'owner') return true;
  if (props.userRole === 'member') {
    return idea.createdBy === props.currentUserId;
  }
  return false;
};

const voteStates = ref<Record<string, { liked: boolean; important: boolean; supported: boolean }>>(
  {}
);
const voteCounts = ref<Record<string, { liked: number; important: number; supported: number }>>({});
const deleteTargetId = ref<string | null>(null);
const showDeleteConfirm = computed({
  get: () => Boolean(deleteTargetId.value),
  set: value => {
    if (!value) deleteTargetId.value = null;
  },
});

const visibleVotes = computed(() => props.visibleVoteTypes);
const voteColumns = computed(() => visibleVotes.value);

// Grid templates: Idea | (Tags+Comments lg only) | Total | Vote columns... | Actions
const baseGridTemplate = computed(() => {
  const voteCount = 1 + voteColumns.value.length;
  // No comments column on narrow screens
  return `2fr ${Array.from({ length: voteCount }, () => '0.7fr').join(' ')} 0.9fr`;
});

const lgGridTemplate = computed(() => {
  const voteCount = 1 + voteColumns.value.length;
  // Include comments column on lg screens (0.6fr width)
  return `2fr 1.2fr 0.6fr ${Array.from({ length: voteCount }, () => '0.7fr').join(' ')} 0.9fr`;
});

const totalScore = (idea: IdeaWithVotes) => {
  const counts = voteCounts.value[idea.id] || {
    liked: idea.liked,
    important: idea.important,
    supported: idea.supported,
  };
  return visibleVotes.value.reduce((sum, type) => sum + (counts[type] || 0), 0);
};

const getCounts = (idea: IdeaWithVotes) =>
  voteCounts.value[idea.id] || {
    liked: idea.liked,
    important: idea.important,
    supported: idea.supported,
  };

const getStates = (idea: IdeaWithVotes) =>
  voteStates.value[idea.id] || {
    liked: idea.userVotes?.liked ?? false,
    important: idea.userVotes?.important ?? false,
    supported: idea.userVotes?.supported ?? false,
  };

function toggleVote(idea: IdeaWithVotes, type: 'liked' | 'important' | 'supported') {
  // Check if votes are locked
  if (props.votesLocked) return;

  const currentStates = getStates(idea);
  const currentCounts = getCounts(idea);
  const nextState = !currentStates[type];
  const nextCount = currentCounts[type] + (nextState ? 1 : -1);

  voteStates.value = {
    ...voteStates.value,
    [idea.id]: { ...currentStates, [type]: nextState },
  };
  voteCounts.value = {
    ...voteCounts.value,
    [idea.id]: { ...currentCounts, [type]: nextCount },
  };

  emit('vote', idea.id, type, () => {
    voteStates.value = {
      ...voteStates.value,
      [idea.id]: currentStates,
    };
    voteCounts.value = {
      ...voteCounts.value,
      [idea.id]: currentCounts,
    };
  });
}

const columnMeta = {
  total: { label: t('filters.totalScore'), sort: 'total' as SortOption },
  liked: { label: t('votes.liked'), sort: 'liked' as SortOption },
  important: { label: t('votes.important'), sort: 'important' as SortOption },
  supported: { label: t('votes.supported'), sort: 'supported' as SortOption },
};

const iconForType = (type: 'liked' | 'important' | 'supported') => {
  if (type === 'liked') return Heart;
  if (type === 'important') return AlertCircle;
  return HandHelping;
};

function setSort(sort: SortOption) {
  emit('update:sortBy', sort);
}

function handleTagClick(tag: string) {
  emit('tagClick', tag);
}

function requestDelete(ideaId: string) {
  deleteTargetId.value = ideaId;
}

function confirmDelete() {
  if (!deleteTargetId.value) return;
  emit('delete', deleteTargetId.value);
  deleteTargetId.value = null;
}

function cancelDelete() {
  deleteTargetId.value = null;
}

watch(
  () => props.ideas,
  ideas => {
    const nextStates: Record<string, { liked: boolean; important: boolean; supported: boolean }> =
      {};
    const nextCounts: Record<string, { liked: number; important: number; supported: number }> = {};
    ideas.forEach(idea => {
      nextStates[idea.id] = {
        liked: idea.userVotes?.liked ?? false,
        important: idea.userVotes?.important ?? false,
        supported: idea.userVotes?.supported ?? false,
      };
      nextCounts[idea.id] = {
        liked: idea.liked,
        important: idea.important,
        supported: idea.supported,
      };
    });
    voteStates.value = nextStates;
    voteCounts.value = nextCounts;
  },
  { immediate: true }
);
</script>

<template>
  <div class="rounded-lg border border-black/10 bg-white/85 overflow-hidden">
    <div
      class="grid grid-cols-[var(--grid-base)] lg:grid-cols-[var(--grid-lg)] gap-3 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-600 bg-black/5"
      :style="{ '--grid-base': baseGridTemplate, '--grid-lg': lgGridTemplate }"
    >
      <span>{{ $t('filters.idea') }}</span>
      <span class="hidden lg:block">{{ $t('idea.tags') }}</span>
      <span class="hidden lg:block text-center">{{ $t('comments.comments') }}</span>
      <button
        type="button"
        class="flex items-center gap-1 text-left hover:text-gray-800 transition-colors"
        :class="{ 'text-gray-800': sortBy === columnMeta.total.sort }"
        @click.stop="setSort('total')"
        title="Sort by total"
      >
        {{ columnMeta.total.label }}
      </button>
      <button
        v-for="type in voteColumns"
        :key="type"
        type="button"
        class="flex items-center gap-1 text-left hover:text-gray-800 transition-colors"
        :class="{ 'text-gray-800': sortBy === columnMeta[type].sort }"
        @click.stop="setSort(columnMeta[type].sort)"
        :title="`Sort by ${columnMeta[type].label.toLowerCase()}`"
      >
        <component :is="iconForType(type)" class="w-3 h-3" />
        {{ columnMeta[type].label }}
      </button>
      <span class="text-right">{{ $t('filters.actions') }}</span>
    </div>
    <div
      v-for="idea in ideas"
      :key="idea.id"
      class="grid grid-cols-[var(--grid-base)] lg:grid-cols-[var(--grid-lg)] gap-3 px-4 py-2 text-sm border-t border-black/10 items-center"
      :style="{ '--grid-base': baseGridTemplate, '--grid-lg': lgGridTemplate }"
    >
      <div class="min-w-0">
        <div class="font-medium text-gray-800">{{ idea.title }}</div>
        <div v-if="idea.person" class="text-xs text-gray-500">
          {{ $t('idea.by') }} {{ idea.person }}
        </div>
        <div v-if="idea.explanation" class="text-xs text-gray-600 line-clamp-2 mt-0.5">
          {{ idea.explanation }}
        </div>
      </div>
      <div class="hidden lg:flex flex-wrap gap-1">
        <button
          v-for="tag in idea.tags"
          :key="tag"
          @click.stop="handleTagClick(tag)"
          class="px-2 py-0.5 text-xs rounded-full bg-black/5 text-gray-700 hover:bg-black/10 transition-colors capitalize"
        >
          {{ tag }}
        </button>
        <span v-if="idea.tags.length === 0" class="text-xs text-gray-400">None</span>
      </div>

      <!-- Comments column - lg only -->
      <button
        class="hidden lg:flex items-center justify-center gap-1 text-xs transition-colors"
        :class="
          commentsEnabled
            ? 'text-gray-600 hover:text-gray-800 cursor-pointer'
            : 'text-gray-400 cursor-default'
        "
        @click.stop="commentsEnabled && $emit('comment', idea.id)"
        :title="commentsEnabled ? $t('comments.view') : $t('comments.disabled')"
      >
        <MessageSquare class="w-3.5 h-3.5" />
        <span v-if="(idea.commentCount || 0) > 0" class="font-semibold">{{
          idea.commentCount
        }}</span>
        <span v-else class="text-gray-400">-</span>
      </button>

      <div class="text-xs font-semibold text-gray-700 text-center">
        {{ totalScore(idea) }}
      </div>
      <div
        v-for="type in voteColumns"
        :key="`count-${type}`"
        class="text-xs font-semibold text-gray-700 text-center"
      >
        {{ getCounts(idea)[type] }}
      </div>
      <div class="flex items-center justify-end gap-2">
        <button
          v-if="visibleVotes.includes('liked')"
          @click.stop="toggleVote(idea, 'liked')"
          :disabled="votesLocked"
          :class="[
            'p-1.5 rounded-md transition-all duration-150 flex items-center justify-center',
            votesLocked
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed opacity-50'
              : getStates(idea).liked
                ? 'bg-red-100 text-(--color-dot-liked)'
                : 'bg-gray-100 text-gray-400 hover:bg-gray-200',
          ]"
          :title="votesLocked ? $t('workshop.votingLocked') : 'Toggle Like'"
        >
          <Heart class="w-4 h-4" />
        </button>
        <button
          v-if="visibleVotes.includes('important')"
          @click.stop="toggleVote(idea, 'important')"
          :disabled="votesLocked"
          :class="[
            'p-1.5 rounded-md transition-all duration-150 flex items-center justify-center',
            votesLocked
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed opacity-50'
              : getStates(idea).important
                ? 'bg-amber-100 text-(--color-dot-important)'
                : 'bg-gray-100 text-gray-400 hover:bg-gray-200',
          ]"
          :title="votesLocked ? $t('workshop.votingLocked') : 'Toggle Important'"
        >
          <AlertCircle class="w-4 h-4" />
        </button>
        <button
          v-if="visibleVotes.includes('supported')"
          @click.stop="toggleVote(idea, 'supported')"
          :disabled="votesLocked"
          :class="[
            'p-1.5 rounded-md transition-all duration-150 flex items-center justify-center',
            votesLocked
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed opacity-50'
              : getStates(idea).supported
                ? 'bg-blue-100 text-(--color-dot-supported)'
                : 'bg-gray-100 text-gray-400 hover:bg-gray-200',
          ]"
          :title="votesLocked ? $t('workshop.votingLocked') : 'Toggle Support'"
        >
          <HandHelping class="w-4 h-4" />
        </button>
        <button
          v-if="commentsEnabled"
          @click.stop="$emit('comment', idea.id)"
          class="p-1.5 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 transition-all duration-150"
          :title="$t('comments.add')"
        >
          <MessageSquare class="w-4 h-4" />
        </button>
        <button
          v-if="canEditIdea(idea)"
          @click.stop="emit('edit', idea.id)"
          class="p-1.5 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 transition-all duration-150"
          title="Edit Idea"
        >
          <Edit class="w-4 h-4" />
        </button>
        <button
          v-if="canEditIdea(idea)"
          @click.stop="requestDelete(idea.id)"
          class="p-1.5 rounded-md bg-gray-100 text-(--color-destructive) hover:bg-red-100 transition-all duration-150"
          title="Delete Idea"
        >
          <Trash2 class="w-4 h-4" />
        </button>
      </div>
    </div>
  </div>
  <ConfirmDialog
    v-model="showDeleteConfirm"
    title="Delete Idea?"
    message="Are you sure you want to delete this idea? This action cannot be undone."
    confirm-text="Delete"
    cancel-text="Cancel"
    confirm-color="destructive"
    @confirm="confirmDelete"
    @cancel="cancelDelete"
  />
</template>
